// Company Brain — pure signal derivation. No React, no fetch: everything here
// takes the raw payloads the existing dashboard endpoints already return and
// turns them into the brain's health score, ranked move queue, live updates
// feed and command interpretations. Kept pure so brainSignals.check.mjs can
// run it under plain `node`.

const clamp = (n, lo, hi) => Math.max(lo, Math.min(hi, n));
export const fmtH = (mins) => {
  const m = Math.max(0, Math.round(mins || 0));
  return m >= 60 ? `${Math.floor(m / 60)}h ${m % 60}m` : `${m}m`;
};

/* ── Health score ─────────────────────────────────────────────────────────
   One number the brain pulses with. Weighted blend of the signals we can
   compute deterministically; weights renormalize when a component is missing
   (e.g. no goals module) so the score never silently reads low. */
export function computeHealth({ kpi, trend = [], projects = [], goalScore = null }) {
  const parts = [];
  if (kpi && kpi.totalMinutes > 0) parts.push({ w: 0.45, v: kpi.utilization || 0 });
  if (kpi && kpi.teamSize > 0) parts.push({ w: 0.25, v: (kpi.activeMembers / kpi.teamSize) * 100 });
  if (projects.length) parts.push({ w: 0.15, v: (projects.filter((p) => p.isActive).length / projects.length) * 100 });
  if (trend.length >= 3) {
    const vals = trend.map((d) => d.minutes || 0);
    const last = vals[vals.length - 1];
    const avg = vals.slice(0, -1).reduce((a, b) => a + b, 0) / (vals.length - 1);
    // Trend contributes direction: on-pace = 100, halved pace = 0.
    parts.push({ w: 0.1, v: avg > 0 ? clamp((last / avg) * 100, 0, 100) : 50 });
  }
  if (goalScore != null) parts.push({ w: 0.1, v: goalScore });
  if (!parts.length) return null;
  const wSum = parts.reduce((a, p) => a + p.w, 0);
  return clamp(Math.round(parts.reduce((a, p) => a + p.w * p.v, 0) / wSum), 2, 99);
}

/* ── Move queue ───────────────────────────────────────────────────────────
   Ranked "act on this" list. Two polarities: drag (issues costing hours) and
   growth (moves that unlock capacity). Impact is in hours wherever we can
   compute one, so the ranking is defensible, not vibes. */
const SEV_RANK = { critical: 4, high: 3, medium: 2, growth: 1 };

export function buildMoveQueue({ members = [], projects = [], insightEntries = [], kpi = null, trend = [] }) {
  const moves = [];

  // Idle drag: idle ≥25% of tracked and ≥1h absolute.
  for (const m of members) {
    const total = (m.activeMinutes || 0) + (m.idleMinutes || 0);
    if (total > 0 && m.idleMinutes >= 60 && m.idleMinutes / total >= 0.25) {
      moves.push({
        id: `idle-${m.userId}`, kind: "idle",
        severity: m.idleMinutes / total >= 0.45 ? "critical" : "high",
        title: `${m.name} idle ${Math.round((m.idleMinutes / total) * 100)}% of tracked time`,
        evidence: `${fmtH(m.idleMinutes)} idle against ${fmtH(total)} tracked in this period.`,
        impact: m.idleMinutes / 60,
        subject: { type: "member", id: m.userId, name: m.name },
        action: { label: "Review idle", type: "member" },
      });
    }
  }

  // Silent members: on the team but nothing tracked in the period.
  const silent = members.filter((m) => !m.hasData);
  if (silent.length) {
    const one = silent.length === 1;
    moves.push({
      id: "silent", kind: "silent", severity: "high",
      title: one ? `${silent[0].name} has no tracked time` : `${silent.length} members have no tracked time`,
      evidence: one
        ? `${silent[0].position || "Member"} — no sessions in this period.`
        : `${silent.slice(0, 3).map((m) => m.name.split(" ")[0]).join(", ")}${silent.length > 3 ? "…" : ""} reported nothing in this period.`,
      impact: silent.length,
      subject: one
        ? { type: "member", id: silent[0].userId, name: silent[0].name }
        : { type: "org", id: "team", name: "Team" },
      action: { label: one ? "Check in" : "Open team", type: one ? "member" : "team" },
    });
  }

  // Stalled projects: marked active-team but no time landed on them.
  for (const p of projects) {
    if (!p.isActive && (p.members || 0) > 0) {
      moves.push({
        id: `stalled-${p._id}`, kind: "stalled", severity: "medium",
        title: `${p.name} has stalled`,
        evidence: `${p.members} member${p.members === 1 ? "" : "s"} assigned, no time tracked in this period.`,
        impact: p.members,
        subject: { type: "project", id: p._id, name: p.name },
        action: { label: "Open project", type: "project" },
      });
    }
  }

  // AI anomalies + growth ideas from the daily summaries (brain-ops feed).
  // One move per person per polarity — the queue ranks people, the updates
  // feed carries the full stream.
  const seen = new Set();
  for (const e of insightEntries) {
    const key = `${e.type}-${e.user?.id}`;
    if (!e.user?.id || seen.has(key)) continue;
    seen.add(key);
    moves.push({
      id: `ai-${key}-${e.date}`,
      kind: e.type === "anomaly" ? "anomaly" : "growth",
      severity: e.type === "anomaly" ? "medium" : "growth",
      title: e.type === "anomaly" ? `Pattern flagged on ${e.user.name}` : `Growth signal from ${e.user.name}`,
      evidence: String(e.content || "").slice(0, 140),
      impact: (e.hoursAnalyzed || 1),
      subject: { type: "member", id: e.user.id, name: e.user.name },
      action: { label: e.type === "anomaly" ? "Investigate" : "Amplify", type: "member" },
    });
  }

  // Org pace: latest day well under the period's own average.
  if (trend.length >= 4) {
    const vals = trend.map((d) => d.minutes || 0);
    const last = vals[vals.length - 1];
    const avg = vals.slice(0, -1).reduce((a, b) => a + b, 0) / (vals.length - 1);
    if (avg >= 60 && last < avg * 0.6) {
      moves.push({
        id: "pace", kind: "trend", severity: "high",
        title: "Team pace dropped vs its own average",
        evidence: `Latest day ${fmtH(last)} against a ${fmtH(avg)} running average.`,
        impact: (avg - last) / 60,
        subject: { type: "org", id: "org", name: "Org" },
        action: { label: "See trend", type: "summary" },
      });
    }
  }

  moves.sort((a, b) => (SEV_RANK[b.severity] - SEV_RANK[a.severity]) || (b.impact - a.impact));
  return moves;
}

/* ── Live updates feed ────────────────────────────────────────────────────
   Right-rail stream: newest first. AI insights interleaved with live
   "tracking now" presence derived from lastActivity. */
export function buildUpdates({ insightEntries = [], members = [], now = Date.now() }) {
  const updates = [];
  for (const m of members) {
    if (!m.lastActivity) continue;
    const ts = new Date(m.lastActivity).getTime();
    const ago = now - ts;
    if (ago < 0) continue;
    if (ago <= 15 * 60 * 1000) {
      updates.push({ id: `live-${m.userId}`, kind: "live", ts, who: m.name, text: `${m.name} is tracking now`, sub: m.position || "" });
    } else if (ago <= 24 * 60 * 60 * 1000) {
      // Recent presence keeps the feed honest when nobody is live this minute.
      updates.push({ id: `recent-${m.userId}`, kind: "recent", ts, who: m.name, text: `${m.name} was active`, sub: m.position || "" });
    }
  }
  for (const e of insightEntries) {
    updates.push({
      id: `ins-${e.type}-${e.user?.id}-${e.ts}`,
      kind: e.type === "anomaly" ? "anomaly" : "growth",
      ts: e.ts, who: e.user?.name || "Someone",
      text: String(e.content || ""), sub: e.user?.position || "",
    });
  }
  updates.sort((a, b) => b.ts - a.ts);
  return updates.slice(0, 30);
}

// Flatten the brain-ops `insights: { [date]: [entry] }` map into a sortable list.
export function flattenInsights(insightsByDate = {}) {
  const out = [];
  for (const [date, list] of Object.entries(insightsByDate)) {
    for (const e of list || []) {
      out.push({ ...e, date, ts: new Date(e.processedAt || `${date}T12:00:00`).getTime() });
    }
  }
  out.sort((a, b) => b.ts - a.ts);
  return out;
}

/* ── Command parsing (voice + text share this) ────────────────────────────
   Deterministic, first-match-wins. Anything we can't resolve locally becomes
   an Ask TG question — the brain never dead-ends a command. */
const MODULE_ALIASES = [
  { re: /\b(classic|old)\s*(dashboard|view)?\b/, to: "classic" },
  { re: /\bprojects?\b/, to: "projects" },
  { re: /\borg\s*(structure|chart)\b/, to: "org-structure" },
  { re: /\bteam\b/, to: "team" },
  { re: /\bgoals?\b/, to: "goals" },
  { re: /\binbox|requests?\b/, to: "inbox" },
  { re: /\bsettings?\b/, to: "settings" },
  { re: /\b(hrms|hr|people)\b/, to: "hrms" },
  { re: /\b(crm|pipeline|deals?)\b/, to: "crm" },
  { re: /\b(email|mail)\b/, to: "email-insights" },
  { re: /\b(work\s*)?summar(y|ies)\b/, to: "work-summary" },
  { re: /\b(insights?|intelligence)\b/, to: "org-intelligence" },
];

export function parseCommand(raw, { names = [] } = {}) {
  const text = String(raw || "").trim();
  if (!text) return { type: "none" };
  const low = text.toLowerCase();

  const theme = low.match(/\b(dark|light)\s*(mode|theme)\b/);
  if (theme) return { type: "theme", mode: theme[1] };

  const view = low.match(/\b(orbit|graph|brain|list|feed)\s*view\b/);
  if (view) return { type: "view", view: /orbit|graph|brain/.test(view[1]) ? "orbit" : "list" };

  // "focus on <name>" / bare exact name → focus that person's node.
  const focusMatch = low.match(/^(?:focus(?:\s+on)?|zoom(?:\s+to)?)\s+(.+)$/);
  const nameQuery = focusMatch ? focusMatch[1] : low;
  const hit = names.find((n) => n.name.toLowerCase() === nameQuery)
    || (focusMatch && names.find((n) => n.name.toLowerCase().includes(nameQuery)))
    || names.find((n) => n.name.toLowerCase().split(" ")[0] === nameQuery);
  if (hit && (focusMatch || nameQuery.length >= 3)) return { type: "focus", member: hit };

  const nav = low.match(/^(?:open|go\s*to|show(?:\s+me)?|take\s+me\s+to)\s+(.+)$/);
  if (nav) {
    const target = nav[1];
    for (const m of MODULE_ALIASES) if (m.re.test(target)) return { type: "navigate", to: m.to };
    const person = names.find((n) => n.name.toLowerCase().includes(target));
    if (person) return { type: "focus", member: person };
  }

  return { type: "ask", q: text };
}
